import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  Divider,
  TextField,
  MenuItem,
  Chip,
  Autocomplete,
  Button,
  Alert,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tabs,
  Tab
} from '@mui/material';
import {
  DarkMode as DarkModeIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as AttachMoneyIcon,
  LocalOffer as LocalOfferIcon,
  RestartAlt as RestartAltIcon,
  Save as SaveIcon,
  Key as KeyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Settings as SettingsIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { useContext } from 'react';
import { ColorModeContext } from '../theme/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/authService';
import RiskManagement from '../components/settings/RiskManagement';

const currencies = [
  { value: '$', label: '$ USD' },
  { value: '€', label: '€ EUR' },
  { value: '£', label: '£ GBP' },
  { value: '¥', label: '¥ JPY' },
  { value: '₹', label: '₹ INR' },
  { value: 'C$', label: 'C$ CAD' },
  { value: 'A$', label: 'A$ AUD' },
];

const commonTags = [
  'Breakout', 'Reversal', 'Trend Following', 'Scalp', 'Swing',
  'Day Trade', 'Position', 'High Probability', 'Low Risk',
  'Momentum', 'Support/Resistance', 'Pattern Trade'
];

const SettingsPage = () => {
  const { mode, toggleColorMode } = useContext(ColorModeContext);
  const { settings, updateSetting, resetToDefaults } = useSettings();
  const { setupMFA, verifyMFASetup, user } = useAuth();
  const [showSaved, setShowSaved] = useState(false);
  const [mfaDialog, setMfaDialog] = useState(false);
  const [secretCode, setSecretCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [mfaStep, setMfaStep] = useState(1);
  const [mfaError, setMfaError] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('idToken');
        const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/usersettings', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = response.data;
        Object.keys(data).forEach(key => updateSetting(key, data[key]));
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('idToken');
      console.log('Sending settings:', settings);
      await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/usersettings', { settings }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      console.error('Error response:', error.response?.data);
    }
  };

  const handleReset = () => {
    resetToDefaults();
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 3000);
  };

  const handleMFAToggle = async (enabled) => {
    if (enabled) {
      setMfaDialog(true);
    } else {
      updateSetting('twoFactorAuth', false);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 3000);
    }
  };

  const handleSetupMFA = async () => {
    try {
      setMfaError('');
      const result = await setupMFA();
      if (result.secretCode) {
        setSecretCode(result.secretCode);
        setMfaStep(2);
      }
    } catch (error) {
      setMfaError(error.message || 'Failed to setup MFA');
    }
  };

  const handleVerifyMFA = async () => {
    try {
      setMfaError('');
      if (verificationCode.length !== 6) {
        setMfaError('Please enter a 6-digit code');
        return;
      }
      
      console.log('Verifying code:', verificationCode);
      console.log('Secret code used:', secretCode);
      
      await verifyMFASetup(verificationCode);
      updateSetting('twoFactorAuth', true);
      setMfaDialog(false);
      setMfaStep(1);
      setVerificationCode('');
      setSecretCode('');
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 3000);
      
    } catch (error) {
      console.error('MFA verification error:', error);
      if (error.message.includes('Code mismatch')) {
        setMfaError('Code mismatch. Please ensure your device time is correct and try a new code.');
      } else {
        setMfaError('Verification failed. Please try again.');
      }
    }
  };

  const generateApiKey = async () => {
    try {
      setApiKeyLoading(true);
      const token = localStorage.getItem('idToken');
      const response = await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createAPIKey', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newApiKey = response.data.apiKey;
      setApiKey(newApiKey);
      updateSetting('apiKey', newApiKey);
      
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 3000);
    } catch (error) {
      console.error('Error generating API key:', error);
    } finally {
      setApiKeyLoading(false);
    }
  };

  const copyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 2000);
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box p={{ xs: 2, sm: 3 }}>
      <Box maxWidth={1000} mx="auto">
        <Typography variant="h4" fontWeight="bold" mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          ⚙️ Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Customize your trading journal experience
        </Typography>
        
        {/* Risk Management tab hidden for now */}
        <Tabs 
          value={0} 
          onChange={handleTabChange} 
          sx={{ mb: 3, display: 'none' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<SettingsIcon />} label="General" />
        </Tabs>

        {showSaved && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Settings saved successfully!
          </Alert>
        )}

        {
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {/* Trading Defaults */}
            <Grid item xs={12}>
              <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                  📊 Trading Defaults
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Set default values for new trades
                </Typography>
                
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Default Position Size"
                      type="number"
                      value={settings.defaultPositionSize || ''}
                      onChange={(e) => updateSetting('defaultPositionSize', Number(e.target.value))}
                      InputProps={{
                        startAdornment: <AccountBalanceIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                      helperText="Default position size in your base currency"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Default Currency"
                      value={settings.defaultCurrency || '$'}
                      onChange={(e) => updateSetting('defaultCurrency', e.target.value)}
                      InputProps={{
                        startAdornment: <AttachMoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                      helperText="Your primary trading currency"
                    >
                      {currencies.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <LocalOfferIcon sx={{ color: 'text.secondary' }} />
                      <Typography variant="body1" fontWeight="medium">
                        Default Tags
                      </Typography>
                    </Box>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={commonTags}
                      value={settings.defaultTags || commonTags}
                      onChange={(event, newValue) => updateSetting('defaultTags', newValue)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...chipProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={key}
                              variant="outlined"
                              label={option}
                              {...chipProps}
                              size="small"
                              sx={{ borderRadius: '16px' }}
                            />
                          );
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Add default tags for new trades"
                          helperText="Tags that will be automatically added to new trades"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* API Access */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                  🔑 API Access
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Generate API key for external integrations and third-party applications
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: 2, flexDirection: { xs: 'column', sm: 'row' }, width: '100%' }}>
                  <TextField
                    fullWidth
                    label="API Key"
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey || 'No API key generated'}
                    InputProps={{
                      readOnly: true,
                      startAdornment: <KeyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      endAdornment: (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {apiKey && (
                            <>
                              <IconButton
                                onClick={() => setShowApiKey(!showApiKey)}
                                edge="end"
                                size="small"
                              >
                                {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                              <IconButton
                                onClick={copyApiKey}
                                edge="end"
                                size="small"
                              >
                                <ContentCopyIcon />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      )
                    }}
                    helperText="Use this key to authenticate API requests to your trading data"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
                  <Button
                    variant={apiKey ? 'outlined' : 'contained'}
                    startIcon={apiKey ? <RefreshIcon /> : <KeyIcon />}
                    onClick={generateApiKey}
                    disabled={apiKeyLoading}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    {apiKey ? 'Regenerate API Key' : 'Generate API Key'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* App Preferences */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                  🎨 App Preferences
                </Typography>
                
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <DarkModeIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Dark Mode" 
                      secondary="Switch between light and dark themes"
                    />
                    <Switch 
                      edge="end"
                      checked={mode === 'dark'}
                      onChange={toggleColorMode}
                    />
                  </ListItem>
                  
                  <Divider />
                  
                  <ListItem>
                    <ListItemIcon>
                      <NotificationsIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Notifications" 
                      secondary="Enable push notifications for trade alerts"
                    />
                    <Switch 
                      edge="end"
                      checked={settings.notifications || false}
                      onChange={(e) => updateSetting('notifications', e.target.checked)}
                    />
                  </ListItem>
                  
                  <Divider />
                  
                  <ListItem>
                    <ListItemIcon>
                      <SecurityIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Two-Factor Authentication" 
                      secondary="Add an extra layer of security to your account"
                    />
                    <Switch 
                      edge="end"
                      checked={settings.twoFactorAuth || false}
                      onChange={(e) => handleMFAToggle(e.target.checked)}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'center', sm: 'flex-end' }, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Tooltip title="Reset all settings to default values">
                <Button
                  variant="outlined"
                  startIcon={<RestartAltIcon />}
                  onClick={handleReset}
                  color="warning"
                  fullWidth
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Reset to Defaults
                </Button>
              </Tooltip>
              
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                fullWidth
                sx={{ minWidth: 120, width: { xs: '100%', sm: 'auto' } }}
              >
                Save Settings
              </Button>
            </Box>
            </Grid>
          </Grid>
        }

        <Dialog 
          open={mfaDialog} 
          onClose={() => setMfaDialog(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'background.paper',
              color: 'text.primary'
            }
          }}
        >
          <DialogTitle sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
            Setup Two-Factor Authentication
          </DialogTitle>
          <DialogContent sx={{ bgcolor: 'background.paper', px: { xs: 2, sm: 3 } }}>
            {mfaError && <Alert severity="error" sx={{ mb: 2 }}>{mfaError}</Alert>}
            
            {mfaStep === 1 && (
              <Box>
                <Typography variant="body1" gutterBottom>
                  Step 1: We'll generate a code for your authenticator app
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You'll need an authenticator app like Google Authenticator or Authy
                </Typography>
              </Box>
            )}
            
            {mfaStep === 2 && (
              <Box>
                <Typography variant="body1" gutterBottom>
                  Step 2: Scan QR code or add manually to your authenticator app
                </Typography>
                {secretCode && (
                  <Box sx={{ textAlign: 'center', my: 3 }}>
                    <Box sx={{ 
                      display: 'inline-block', 
                      p: 2, 
                      bgcolor: 'white', 
                      borderRadius: 2,
                      border: '1px solid #ddd'
                    }}>
                      <img 
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=otpauth://totp/Trading%2520Journal:${encodeURIComponent(user?.attributes?.email || 'user')}?secret=${secretCode}&issuer=Trading%2520Journal`}
                        alt="QR Code for 2FA setup"
                        style={{ width: '100%', maxWidth: 200, height: 'auto', maxHeight: 200 }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ mt: 2, mb: 1 }} color="text.secondary">
                      Or enter this code manually:
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: mode === 'dark' ? 'grey.800' : 'grey.100', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                        {secretCode}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
            
            {mfaStep === 3 && (
              <Box>
                <Typography variant="body1" gutterBottom>
                  Step 3: Enter the 6-digit code from your authenticator app
                </Typography>
                <TextField
                  fullWidth
                  label="Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  margin="normal"
                  placeholder="000000"
                  inputProps={{ maxLength: 6 }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ bgcolor: 'background.paper' }}>
            <Button onClick={() => setMfaDialog(false)}>Cancel</Button>
            {mfaStep === 1 && (
              <Button onClick={handleSetupMFA} variant="contained">
                Generate Code
              </Button>
            )}
            {mfaStep === 2 && (
              <Button onClick={() => setMfaStep(3)} variant="contained">
                I've Added the Code
              </Button>
            )}
            {mfaStep === 3 && (
              <Button onClick={handleVerifyMFA} variant="contained">
                Verify & Enable 2FA
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default SettingsPage;